import {
  createContext,
  type ReactNode,
  use,
  useSyncExternalStore,
} from 'react';

import type { FeatureFlags, FlagName, FlagValues } from '../index';

// The server snapshot is the same: React Router prerenders the SPA shell at build time,
// and useSyncExternalStore throws on the server without it.

const FeatureFlagsContext = createContext<FeatureFlags<FlagName> | null>(null);

type FeatureFlagsProviderProps = Readonly<{
  flags: FeatureFlags<FlagName>;
  children: ReactNode;
}>;

export function FeatureFlagsProvider({
  flags,
  children,
}: FeatureFlagsProviderProps) {
  return <FeatureFlagsContext value={flags}>{children}</FeatureFlagsContext>;
}

/** The store itself, e.g. to set runtime values. */
export function useFeatureFlagsStore(): FeatureFlags<FlagName> {
  const flags = use(FeatureFlagsContext);

  if (!flags) {
    throw new Error('Feature flags are used outside of FeatureFlagsProvider');
  }

  return flags;
}

/** All values. Re-renders when any flag changes, prefer useFeatureFlag for a single flag. */
export function useFeatureFlags(): FlagValues<FlagName> {
  const flags = useFeatureFlagsStore();

  return useSyncExternalStore(
    flags.subscribe,
    flags.getSnapshot,
    flags.getSnapshot
  );
}

/** Re-renders only when this flag changes, not when other flags do. */
export function useFeatureFlag(name: FlagName): boolean {
  const flags = useFeatureFlagsStore();

  const getSnapshot = () => flags.isEnabled(name);

  return useSyncExternalStore(flags.subscribe, getSnapshot, getSnapshot);
}

type FeatureProps = Readonly<{
  name: FlagName;
  children: ReactNode;
  /** Rendered while the flag is off. */
  fallback?: ReactNode;
}>;

export function Feature({ name, children, fallback = null }: FeatureProps) {
  return useFeatureFlag(name) ? children : fallback;
}
